import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import type { RootState } from "redux/store";
import { useGetCategoryRoomsQuery } from "api/categoryRooms/categoryRoomsApiSlice";
import RoomWeekView from "../components/RoomWeekView";
import { planRoomGroups, planRoomLabel, planRoomShown } from "../publicPlan";

/**
 * `/plan?category=<key>[&objectId=<groupId>]` — the public plan of a category's
 * rooms (#3075, completion plan W0-4), made to be embedded in the tenant's
 * website with an iframe, or linked to.
 *
 * - **What a visitor sees** is the room week view as the app shows it to
 *   anyone signed out: free and occupied, and an organisation's name only
 *   where the tenant ticked "may appear by name on the public plan" for it
 *   (completion plan D3b; `docs/architecture/room-week-view.md`, "Who sees
 *   what"). Every other booking shows without a name.
 * - **No consent page first** (`routes/consentExempt.ts`): read-only, it
 *   stores nothing about the visitor; booking opens the full app, which asks.
 * - **The room** is chosen in a dropdown grouped by city. `objectId=` in the link
 *   preselects one; the choice is kept in the URL, so a reload keeps it.
 * - **Booking** opens the app in a new window: inside a website's frame the
 *   booking flow would be cramped and cut off.
 *
 * The rooms are the category's room list (`GET /app/categories/:key/rooms`),
 * the same the object-first entry shows, so members-only rooms stay hidden
 * from visitors.
 */
const PublicPlanPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryKey = searchParams.get("category")?.trim() || "";
  const viewerId = useSelector((state: RootState) => state.account?.profile?.id ?? null);

  const { data: rooms, error, isLoading } = useGetCategoryRoomsQuery(
    { categoryKey, viewerId },
    { skip: !categoryKey }
  );

  const shown = rooms ? planRoomShown(rooms, searchParams.get("objectId")) : null;

  const pickRoom = (groupId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("objectId", groupId);
    setSearchParams(next, { replace: true });
  };

  let body: React.ReactNode;
  if (!categoryKey) {
    body = <p role="alert">{t("public_plan.no_category")}</p>;
  } else if (isLoading) {
    body = (
      <p className="flex items-center gap-2 text-foreground/60">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
      </p>
    );
  } else if (error || !rooms) {
    // A failure is never an empty plan: say that it failed (#1970).
    const status = (error as { status?: number | string } | undefined)?.status;
    body = (
      <p role="alert">
        {t(status === 404 ? "public_plan.category_not_found" : "public_plan.load_failed")}
      </p>
    );
  } else if (!shown) {
    body = <p role="status">{t("public_plan.no_rooms")}</p>;
  } else {
    body = (
      <div className="flex flex-col gap-4">
        <div className="flex max-w-md flex-col gap-1">
          <label htmlFor="public-plan-room" className="text-sm font-medium text-foreground">
            {t("public_plan.room_label")}
          </label>
          <Select value={shown.groupId} onValueChange={pickRoom}>
            <SelectTrigger id="public-plan-room">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {planRoomGroups(rooms).map((group) => (
                <SelectGroup key={group.city ?? ""}>
                  {group.city && <SelectLabel>{group.city}</SelectLabel>}
                  {[...group.rooms]
                    // By label, so a hall's segments sit under the hall.
                    .sort((a, b) =>
                      planRoomLabel(a, i18n.language).localeCompare(planRoomLabel(b, i18n.language))
                    )
                    .map((room) => (
                    <SelectItem key={room.groupId} value={room.groupId}>
                      {planRoomLabel(room, i18n.language)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>
        <RoomWeekView groupId={shown.groupId} bookIn="newWindow" />
      </div>
    );
  }

  return <div className="mx-auto w-full max-w-7xl px-4 pb-6 pt-4 text-sm text-foreground">{body}</div>;
};

export default PublicPlanPage;
